/**
 * The inside-corner REVEAL takes the leftover beside it.
 *
 * A blind or empty corner puts a 3" filler on each leg it trims
 * (`INSIDE_CORNER_REVEAL`, `fillRun`'s `emitCornerReveal`), so the first door
 * on that leg clears the face of the unit across the corner. The leg is then
 * packed from past the reveal, and the stretch's own leftover goes where
 * `fillerEnd.js` puts it — at the corner end, right against the reveal. Two
 * fillers side by side: on 444 of 2,819 generated layouts, and on two in
 * three once a user picks a blind corner (census 2026-10-26; Cabin at 2428
 * Lore Rd, 3" + 5 7/16" between a blind base and a sink base).
 *
 * User ruling 2026-10-26: 3" is the NARROWEST the corner filler may be. So the
 * reveal grows by whatever stands beside it that is not a part — a remainder
 * filler, or the bare slot a leftover under a filler's width leaves — and the
 * leg has one filler. A cabinet still goes in wherever the stretch past the
 * reveal holds one (≥ 9"), because the stretch is packed exactly as before;
 * only what it leaves over moves.
 *
 * Pure: 1-D params in, pieces out.
 */

const { SPAN_EPS } = require('./spanPacking');

/** A packer's remainder filler — not a user's filler bay, which is a part. */
const isRemainderFiller = (p) => p.kind === 'filler' && !p.override;

/**
 * Where the leftover beside the reveal ends, walking in from `edge` (the
 * zone end the reveal stands at): the far side of a remainder filler there,
 * else the edge of whatever stands nearest, else the other end of the zone.
 *
 * @returns {{ to: number, drop: Object|null }} `drop` is the filler absorbed.
 */
function leftoverBeside(pieces, side, zone, padOf) {
  const atHi = side === 'hi';
  const edge = atHi ? zone.hi : zone.lo;
  // The piece nearest the reveal's end of the zone.
  let nearest = null;
  for (const p of pieces) {
    if (!nearest || (atHi ? p.end > nearest.end : p.start < nearest.start))
      nearest = p;
  }
  if (!nearest) return { to: atHi ? zone.lo : zone.hi, drop: null };
  const touches = atHi
    ? Math.abs(nearest.end - edge) <= SPAN_EPS
    : Math.abs(nearest.start - edge) <= SPAN_EPS;
  if (touches && isRemainderFiller(nearest))
    return { to: atHi ? nearest.start : nearest.end, drop: nearest };
  // A hard piece keeps its pad clear (a fridge's surround panels).
  const pad = nearest.kind === 'hard' ? padOf(nearest) : 0;
  return { to: atHi ? nearest.end + pad : nearest.start - pad, drop: null };
}

/**
 * Grow each reveal over the leftover beside it.
 *
 * @param {Object[]} pieces the zone's packed pieces (`planSubSpanSequence`)
 * @param {{ lo: number, hi: number }} zone the packed zone, reveals outside it
 * @param {{ lo?: {start, end}, hi?: {start, end} }} reveals the reveal at
 *   each end of the zone: `lo` ends where the zone starts, `hi` starts where
 *   it ends
 * @param {(piece) => number} padOf a hard piece's kept-clear strip
 * @returns {{ pieces: Object[], reveals: {side, start, end}[] }} the pieces
 *   with each absorbed filler removed and each reveal added as a filler piece
 *   (`reveal: true`), in param order
 */
function absorbLeftoverIntoReveals(pieces, zone, reveals, padOf = () => 0) {
  let out = [...pieces];
  const grown = [];
  for (const side of ['lo', 'hi']) {
    const reveal = reveals[side];
    if (!reveal) continue;
    const { to, drop } = leftoverBeside(out, side, zone, padOf);
    if (drop) out = out.filter((p) => p !== drop);
    // Never past the zone's far end, never shrinking the reveal (an
    // overflowing pack can stand a piece past the zone's end).
    const start =
      side === 'hi'
        ? Math.min(reveal.start, Math.max(zone.lo, to))
        : reveal.start;
    const end =
      side === 'lo' ? Math.max(reveal.end, Math.min(zone.hi, to)) : reveal.end;
    grown.push({ side, start, end });
  }
  const revealPieces = grown.map((r) => ({
    kind: 'filler',
    start: r.start,
    end: r.end,
    reveal: true,
  }));
  return {
    pieces: [...out, ...revealPieces].sort((a, b) => a.start - b.start),
    reveals: grown,
  };
}

/**
 * A SINK BASE beside a grown reveal steps up to the line's next size, and the
 * reveal gives it the width (user ruling 2026-10-26: "if a product family has
 * a 39" sink base, then it's fine for the cab engine to use it").
 *
 * The reveal takes in whatever the leg leaves over (above), so beside a sink
 * base — a hard piece the packer cannot resize — it can stand 6" to 12" wide.
 * One step up the line's own sizes (36" to 39" on Venus), only where the
 * reveal keeps its 3" (`minReveal`), and only into the reveal: the base's
 * other edge stays put, so nothing else on the leg moves. One step, never
 * two — the user asked for "another 3" to the width of the next cab over",
 * and 42" stays a choice.
 *
 * @param {Object[]} pieces the zone's pieces, reveals included (`reveal: true`)
 * @param {(piece) => number|null} nextWidthOf the next width (metres) the
 *   line sells for this sink base, or null — also null for any piece this
 *   must not widen
 * @param {number} minReveal the narrowest a reveal may be (metres)
 * @returns {{ pieces: Object[], widened: {bayId, width}[] }} new objects for
 *   every piece it changed; the rest are passed through
 */
function widenSinkBasesIntoReveals(pieces, nextWidthOf, minReveal) {
  let out = [...pieces];
  const widened = [];
  for (const reveal of pieces.filter((p) => p.reveal)) {
    const r = out.indexOf(reveal);
    // The piece on the leg's side of the reveal — the one it was grown up to.
    const beside = out.find(
      (p) =>
        p.kind === 'hard' &&
        p.bay?.kind === 'sink-base' &&
        (Math.abs(p.end - reveal.start) <= SPAN_EPS ||
          Math.abs(p.start - reveal.end) <= SPAN_EPS)
    );
    if (!beside) continue;
    const next = nextWidthOf(beside);
    const was = beside.end - beside.start;
    const grow = next == null ? 0 : next - was;
    if (!(grow > SPAN_EPS)) continue;
    if (reveal.end - reveal.start - grow < minReveal - SPAN_EPS) continue;
    const intoLo = Math.abs(beside.start - reveal.end) <= SPAN_EPS;
    const base = intoLo
      ? { ...beside, start: beside.start - grow }
      : { ...beside, end: beside.end + grow };
    const shrunk = intoLo
      ? { ...reveal, end: reveal.end - grow }
      : { ...reveal, start: reveal.start + grow };
    out = out.map((p) => (p === beside ? base : p));
    out[r] = shrunk;
    widened.push({ bayId: beside.bay.bayId, width: base.end - base.start });
  }
  return { pieces: out, widened };
}

module.exports = { absorbLeftoverIntoReveals, widenSinkBasesIntoReveals };
